import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WhatsAppIcon } from './icons';
import { waUrl, waMessages, reportConversion } from '../utils/leads';

const values = [
  {
    title: 'We ship, fast',
    text: 'Working software in days, not month-long discovery phases. You see real progress from week one.',
  },
  {
    title: 'Products, not pages',
    text: 'We build and run our own products — KiranaGo, Beacon, AllSecure VPN. Your project gets the same engineering.',
  },
  {
    title: 'Plain talk',
    text: 'No jargon, no inflated scope. We tell you what your business actually needs — sometimes that costs less, not more.',
  },
  {
    title: 'Your code, your keys',
    text: 'Everything we build is handed over completely. Code, accounts, domains. You are never locked in.',
  },
];

const About: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ctaUrl = waUrl(waMessages.general);

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] glow-ember pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <span className="eyebrow">About us</span>
          <h1 className="font-display font-bold text-4xl md:text-6xl tracking-tight mt-5 mb-6 leading-[1.05]">
            A small studio that builds{' '}
            <span className="text-gradient">like a product company.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/65 leading-relaxed max-w-2xl">
            JSONPLAY is an India-based software studio. We design, engineer and
            ship websites, mobile apps and AI products — for our own portfolio
            and for clients from local businesses to international marketplaces.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24 bg-ink-900/40 border-y border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 space-y-6 text-white/70 text-lg leading-relaxed">
          <p>
            Most agencies sell you hours. We don’t. We run JSONPLAY the way we
            build our own products: small scope, fixed price, shipped fast,
            improved continuously.
          </p>
          <p>
            That’s why our portfolio isn’t mockups — it’s live software. A
            quick-commerce delivery app. A social media and ads manager. A VPN
            with its own native engine. A three-app marketplace shipped to the
            App Store for an international client.
          </p>
          <p className="text-white font-medium">
            When you hire us, you get the team that builds those — working on
            your idea.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-10">
            How we work
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {values.map((v) => (
              <div key={v.title} className="glass p-7">
                <h3 className="font-display font-bold text-xl text-ember-glow mb-2">
                  {v.title}
                </h3>
                <p className="text-white/60 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4">
            See if we’re the right fit.
          </h2>
          <p className="text-white/60 text-lg mb-8">
            One message. A free plan and fixed quote within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
              onClick={() => reportConversion(ctaUrl)}
            >
              <WhatsAppIcon className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <Link to="/" className="btn-ghost">
              See our work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
